import axios, { AxiosResponse, AxiosError } from 'axios';
import { toast } from 'react-hot-toast';
import type { ApiResponse } from '@/types/api';
import { loggedOutHref } from '@/lib/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:42817';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('noor-bot-auth');
    if (token) {
      try {
        const authData = JSON.parse(token);
        if (authData?.state?.token) {
          config.headers.Authorization = `Bearer ${authData.state.token}`;
        }
      } catch (error) {
        console.error('Failed to parse auth token:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    return response;
  },
  (error: AxiosError<ApiResponse>) => {
    const message = error.response?.data?.error || error.message || 'An unexpected error occurred';
    
    // Handle specific error codes
    const requestUrl = error.config?.url || '';
    const isAuthAttempt =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/auth/google');

    if (error.response?.status === 401 && !isAuthAttempt) {
      // Session invalid/expired on an authenticated request — clear auth and
      // send the user to the logged-out destination (marketing zone or /login).
      localStorage.removeItem('noor-bot-auth');
      window.location.href = loggedOutHref();
      toast.error('Session expired. Please log in again.');
    } else if (error.response?.status === 401 && isAuthAttempt) {
      // A failed login/register — surface the real message, don't "log out".
      toast.error(message);
    } else if (error.response?.status === 403) {
      toast.error('Access denied');
    } else if (error.response?.status && error.response.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else {
      toast.error(message);
    }

    return Promise.reject(error);
  }
);

// Helper functions for common API operations
export const api = {
  // Generic GET request
  get: <T>(url: string, params?: Record<string, any>) =>
    apiClient.get<ApiResponse<T>>(url, { params }).then(res => res.data.data),

  // Generic POST request
  post: <T>(url: string, data?: any) =>
    apiClient.post<ApiResponse<T>>(url, data).then(res => res.data.data),

  // Generic PUT request
  put: <T>(url: string, data?: any) =>
    apiClient.put<ApiResponse<T>>(url, data).then(res => res.data.data),

  // Generic DELETE request
  delete: <T>(url: string) =>
    apiClient.delete<ApiResponse<T>>(url).then(res => res.data.data),

  // Generic PATCH request
  patch: <T>(url: string, data?: any) =>
    apiClient.patch<ApiResponse<T>>(url, data).then(res => res.data.data),
};

export { apiClient };
export default apiClient;